import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Star, ArrowRight, Plus, Minus, ClipboardList, Puzzle, Gift } from "lucide-react";
import AxosShell from "./AxosShell";
import { Block } from "./Annotate";
import { LANDING_PINS } from "./tests";
import { A, SERIF, SANS, MONO, ADS, SHOTS } from "./brand";

const FAQS = [
  {
    q: "Can I take this alongside levothyroxine?",
    a: "Most people do. Leave four hours between your medication and any supplement containing minerals, and tell your doctor what you are taking. We put the full ingredient list on the label so that conversation is easy.",
  },
  {
    q: "How long before I notice anything?",
    a: "Most reviews describe a change somewhere between week three and week six. Nutrient status does not move in a weekend, so we build the first order as a full thirty days.",
  },
  {
    q: "What is actually in it?",
    a: "Selenium, zinc, vitamin D and methylated B12 at doses you can read off the label, in thirty vegetarian capsules. No proprietary blends and no filler list you need a chemistry degree to check.",
  },
  {
    q: "When will my order arrive?",
    a: "Orders placed before 2pm ship the same working day. Two to three days in the UK, five to seven for the rest of Europe.",
  },
];

const REVIEWS = [
  {
    n: "Marianne T.",
    t: "Hashimoto's, four years",
    q: "I was taking six separate bottles and still felt like I was guessing. This replaced five of them and my last panel was the best it has been.",
  },
  {
    n: "Priya R.",
    t: "Diagnosed 2023",
    q: "Day 28 and I am steadier through the afternoon. That is the part I gave up on ever fixing.",
  },
  {
    n: "Kate D.",
    t: "Subscribed since March",
    q: "The quiz told me which one to start with, which no other brand has ever bothered to do. One capsule and I am done.",
  },
];

export default function AxosLanding() {
  const [open, setOpen] = useState(0);

  return (
    <AxosShell pins={LANDING_PINS} kicker="Axos · quiz landing page">
      <div style={{ background: A.white, color: A.body }}>
        {/* announcement */}
        <div className="px-4 py-2 text-center text-[11.5px]" style={{ background: A.navy, color: "#DCE6F2", letterSpacing: "0.04em" }}>
          Free UK delivery over £50 · Third party tested every batch
        </div>

        {/* nav */}
        <header className="flex items-center justify-between border-b px-5 py-4 sm:px-10" style={{ borderColor: A.line }}>
          <div>
            <div className="text-[22px] leading-none tracking-tight" style={{ fontFamily: SERIF, color: A.navy, fontWeight: 600 }}>
              axos
            </div>
            <div className="mt-1 text-[9px] tracking-[0.14em]" style={{ color: A.soft }}>
              SUPPORT THE CONNECTION.
            </div>
          </div>
          <nav className="hidden items-center gap-7 text-[13px] sm:flex" style={{ color: A.ink }}>
            <span>Shop</span>
            <span>The science</span>
            <span>Reviews</span>
            <span>Journal</span>
          </nav>
          <a
            href="#quiz"
            data-testid="nav-quiz-cta"
            className="rounded-full px-4 py-2 text-[12.5px] font-medium"
            style={{ background: A.blush, color: A.white }}
          >
            Take the quiz
          </a>
        </header>

        {/* hero */}
        <Block id="hero" className="mx-auto max-w-[1180px] px-5 pb-14 pt-10 sm:px-10 sm:pb-20 sm:pt-16">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <div className="mb-5 h-[2px] w-[52px]" style={{ background: A.blush }} />
              <h1
                className="text-4xl leading-[1.02] sm:text-5xl lg:text-6xl"
                style={{ fontFamily: SERIF, color: A.navy, fontWeight: 500 }}
              >
                Your supplement
                <br />
                routine,{" "}
                <em style={{ color: A.blushDeep, fontStyle: "italic" }}>simplified</em>
              </h1>
              <p className="mt-5 max-w-[440px] text-base leading-relaxed sm:text-lg" style={{ color: A.body }}>
                Answer three questions. See which Axos routine fits your thyroid, your gut and the medication you are
                already on. Then unlock your offer.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to="/axos/quiz"
                  data-testid="hero-quiz-cta"
                  className="group inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-[15px] font-medium transition-transform"
                  style={{ background: A.blush, color: A.white, boxShadow: "0 12px 28px rgba(233,140,158,0.35)" }}
                >
                  Start the quiz
                  <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <span className="text-[12.5px]" style={{ color: A.soft }}>
                  Three questions. About sixty seconds.
                </span>
              </div>

              <Block id="trustbar" className="mt-10 grid max-w-[560px] grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  ["1,571", "verified reviews"],
                  ["Every batch", "third party tested"],
                  ["Formulated with", "a thyroid dietitian"],
                ].map(([a1, a2]) => (
                  <div key={a2} className="rounded-xl border px-3.5 py-3" style={{ borderColor: A.line, background: A.cream }}>
                    <div className="text-[13px] font-semibold" style={{ color: A.navy }}>
                      {a1}
                    </div>
                    <div className="mt-0.5 text-[12px]" style={{ color: A.soft }}>
                      {a2}
                    </div>
                  </div>
                ))}
              </Block>
            </div>

            <div className="relative">
              <div
                className="overflow-hidden rounded-[26px]"
                style={{ border: `1px solid ${A.line}`, boxShadow: "0 30px 70px rgba(14,33,64,0.16)" }}
              >
                <img src={ADS.guided} alt="Hashi-aid, a more guided way to start" className="block w-full" />
              </div>
              <div
                className="absolute -bottom-5 -left-4 hidden rounded-2xl px-4 py-3 sm:block"
                style={{ background: A.white, border: `1px solid ${A.line}`, boxShadow: "0 14px 34px rgba(14,33,64,0.14)" }}
              >
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={13} fill={A.blush} color={A.blush} />
                  ))}
                </div>
                <div className="mt-1 text-[12px]" style={{ color: A.ink }}>
                  10,000+ choosing Axos
                </div>
              </div>
            </div>
          </div>
        </Block>

        {/* three step strip */}
        <Block id="steps" className="mx-auto mb-16 max-w-[1180px] px-5 sm:px-10">
          <div className="grid gap-3 rounded-2xl px-5 py-7 sm:grid-cols-3 sm:px-8" style={{ background: A.cream, border: `1px solid ${A.line}` }}>
            {[
              [ClipboardList, "Quiz", "Three questions about you", A.teal],
              [Puzzle, "Match", "Your Hashi-aid routine", A.blush],
              [Gift, "Offer", "Unlocked at the end", A.tealSoft],
            ].map(([Icon, t, s, c]) => (
              <div key={t} className="flex items-start gap-3.5">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full" style={{ border: `1.5px solid ${c}` }}>
                  <Icon size={19} color={c} />
                </span>
                <div>
                  <div className="text-[16px]" style={{ fontFamily: SERIF, color: A.navy, fontWeight: 600 }}>
                    {t}
                  </div>
                  <div className="mt-0.5 text-[12.5px]" style={{ color: A.soft }}>
                    {s}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Block>

        {/* mechanism */}
        <Block id="mechanism" className="mx-auto mb-20 max-w-[1180px] px-5 sm:px-10">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="overflow-hidden rounded-[22px]" style={{ border: `1px solid ${A.line}` }}>
              <img src={ADS.mechanism} alt="Designed for the gut and thyroid axis" className="block w-full" />
            </div>
            <div>
              <div className="mb-3 text-[10px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.teal }}>
                Why this is not another bottle
              </div>
              <h2 className="text-base leading-snug md:text-lg" style={{ fontFamily: SERIF, color: A.navy, fontSize: 30, fontWeight: 500 }}>
                Built for the gut and thyroid connection, not one of them
              </h2>
              <p className="mt-4 text-base leading-relaxed" style={{ color: A.body }}>
                Absorption is the part most thyroid supplements ignore. Axos formulates for both sides of the axis, at doses
                you can read off the label.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {[
                  ["Selenium", "200mcg, as selenomethionine"],
                  ["Zinc", "15mg, bisglycinate"],
                  ["Vitamin D3", "2000iu with K2"],
                  ["Methylated B12", "500mcg, methylcobalamin"],
                ].map(([n, d]) => (
                  <div key={n} className="rounded-xl border p-3.5" style={{ borderColor: A.line }}>
                    <div className="text-[14px] font-semibold" style={{ color: A.navy }}>
                      {n}
                    </div>
                    <div className="mt-0.5 text-[12.5px]" style={{ color: A.soft }}>
                      {d}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[12.5px]" style={{ color: A.soft }}>
                One capsule a day. Thirty vegetarian capsules per bottle.
              </p>
            </div>
          </div>
        </Block>

        {/* comparison */}
        <Block id="compare" className="px-5 py-16 sm:px-10" style={{ background: A.navy }}>
          <div className="mx-auto max-w-[900px]">
            <h2
              className="mb-8 text-center"
              style={{ fontFamily: SERIF, color: A.white, fontSize: 30, fontWeight: 500, lineHeight: 1.15 }}
            >
              A more <em style={{ color: A.blush }}>guided</em> way to start
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}>
                <div className="mb-4 text-[11px] uppercase tracking-[0.16em]" style={{ fontFamily: MONO, color: "#93A3B8" }}>
                  Generic thyroid supplements
                </div>
                {[
                  "One size fits all approach",
                  "Random formulas with guesswork",
                  "Missing key thyroid nutrients",
                  "No personalisation or guidance",
                  "Hard to know what is right for you",
                ].map((l) => (
                  <div key={l} className="flex items-start gap-3 border-b py-3 text-[13.5px] last:border-b-0" style={{ borderColor: "rgba(255,255,255,0.08)", color: "#C3CEDC" }}>
                    <X size={15} color="#8A99AD" className="mt-[3px] shrink-0" />
                    {l}
                  </div>
                ))}
              </div>
              <div className="rounded-2xl p-6" style={{ background: "rgba(58,166,166,0.10)", border: `1px solid ${A.teal}55` }}>
                <div className="mb-4 text-[11px] uppercase tracking-[0.16em]" style={{ fontFamily: MONO, color: A.tealSoft }}>
                  Axos Hashi-aid
                </div>
                {[
                  "Personalised to you with the quiz",
                  "Targeted thyroid focused nutrition",
                  "Selenium, zinc, vitamin D and methylated B12",
                  "Guided by your own answers",
                  "Transparent formulas you can check",
                ].map((l) => (
                  <div key={l} className="flex items-start gap-3 border-b py-3 text-[13.5px] last:border-b-0" style={{ borderColor: "rgba(255,255,255,0.08)", color: "#E6EEF5" }}>
                    <Check size={15} color={A.tealSoft} className="mt-[3px] shrink-0" />
                    {l}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/axos/quiz"
                data-testid="compare-quiz-cta"
                className="inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-[14.5px] font-medium"
                style={{ background: A.blush, color: A.white }}
              >
                Get matched
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </Block>

        {/* reviews */}
        <Block id="reviews" className="mx-auto max-w-[1180px] px-5 py-18 sm:px-10" style={{ paddingTop: 72, paddingBottom: 72 }}>
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <h2 style={{ fontFamily: SERIF, color: A.navy, fontSize: 30, fontWeight: 500 }}>What people actually wrote</h2>
            <div className="flex items-center gap-2 text-[12.5px]" style={{ color: A.soft }}>
              <span className="flex items-center gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={12} fill={A.blush} color={A.blush} />
                ))}
              </span>
              4.7 from 1,571 reviews
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {REVIEWS.map((r) => (
              <figure key={r.n} className="rounded-2xl border p-6" style={{ borderColor: A.line, background: A.cream }}>
                <div className="mb-3 flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={12} fill={A.blush} color={A.blush} />
                  ))}
                </div>
                <blockquote className="text-[14.5px] leading-relaxed" style={{ fontFamily: SERIF, color: A.ink }}>
                  “{r.q}”
                </blockquote>
                <figcaption className="mt-4 text-[12px]" style={{ color: A.soft }}>
                  {r.n} · {r.t}
                </figcaption>
              </figure>
            ))}
          </div>
        </Block>

        {/* faq */}
        <Block id="faq" className="mx-auto max-w-[820px] px-5 pb-20 sm:px-10">
          <h2 className="mb-6" style={{ fontFamily: SERIF, color: A.navy, fontSize: 30, fontWeight: 500 }}>
            Questions people ask before they order
          </h2>
          <div className="rounded-2xl border" style={{ borderColor: A.line }}>
            {FAQS.map((f, i) => {
              const live = open === i;
              return (
                <div key={f.q} className="border-b last:border-b-0" style={{ borderColor: A.line }}>
                  <button
                    type="button"
                    data-testid={`faq-${i}`}
                    onClick={() => setOpen(live ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="text-[14.5px]" style={{ color: A.navy, fontWeight: 500 }}>
                      {f.q}
                    </span>
                    {live ? <Minus size={16} color={A.blushDeep} /> : <Plus size={16} color={A.soft} />}
                  </button>
                  {live && (
                    <p className="px-5 pb-5 text-[13.5px] leading-relaxed" style={{ color: A.body }}>
                      {f.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Block>

        {/* closing */}
        <section className="px-5 py-16 sm:px-10" style={{ background: A.cream, borderTop: `1px solid ${A.line}` }}>
          <div className="mx-auto grid max-w-[1000px] items-center gap-8 sm:grid-cols-[1fr_auto]">
            <div>
              <h2 style={{ fontFamily: SERIF, color: A.navy, fontSize: 28, fontWeight: 500 }}>
                Not every routine fits everyone
              </h2>
              <p className="mt-2 text-base" style={{ color: A.body }}>
                Start with the quiz. Sixty seconds, then your match and your offer.
              </p>
            </div>
            <Link
              to="/axos/quiz"
              data-testid="footer-quiz-cta"
              className="inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-[15px] font-medium"
              style={{ background: A.navy, color: A.white }}
            >
              Start the quiz
              <ArrowRight size={16} />
            </Link>
          </div>
        </section>

        <footer className="px-5 py-10 text-[12px] sm:px-10" style={{ background: A.navyDeep, color: "#93A3B8" }}>
          <div className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-between gap-4">
            <span>Axos · quiz.ibdassist.com</span>
            <span>Free UK delivery over £50 · 30 day returns · Third party tested</span>
          </div>
        </footer>

        {/* sticky bar, itself a queued test */}
        <Block
          id="stickycta"
          pin="tr"
          className="fixed bottom-0 left-0 right-0 z-[70] flex items-center justify-between gap-4 px-4 py-3 sm:px-8"
          style={{ background: "rgba(14,33,64,0.96)", backdropFilter: "blur(10px)" }}
        >
          <div className="min-w-0">
            <div className="truncate text-[13px]" style={{ color: A.white }}>
              Hashi-aid · 30 capsules
            </div>
            <div className="text-[11.5px]" style={{ color: "#9FB0C4" }}>
              £39, or £33.15 on subscription
            </div>
          </div>
          <Link
            to="/axos/quiz"
            data-testid="sticky-quiz-cta"
            className="shrink-0 rounded-full px-5 py-2.5 text-[13px] font-medium"
            style={{ background: A.blush, color: A.white }}
          >
            Start the quiz
          </Link>
        </Block>
        <div style={{ height: 68 }} />
      </div>
    </AxosShell>
  );
}
